import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll, PageHeader, Panel, Pill, UrlRow, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function Act({ id, action, label, tone = "default", extra = {}, reload, testid }) {
  const run = async () => {
    try { await api.post(`/admin/lab/${id}/action`, { action, ...extra }); toast.success(`${label}`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cls = tone === "red" ? "border-red-900 text-red-300 hover:bg-red-950" : tone === "green" ? "border-emerald-900 text-emerald-300 hover:bg-emerald-950" : "border-slate-700 text-slate-300";
  return <Button data-testid={testid} size="sm" variant="outline" className={`h-7 text-xs ${cls}`} onClick={run}>{label}</Button>;
}

function LabCard({ l, reload }) {
  const s = l.state || {};
  const [secs, setSecs] = useState(15);
  const p = { id: l.id, reload };
  const del = async () => { await api.delete(`/admin/lab/${l.id}`); reload(); };
  return (
    <Panel testid={`lab-card-${l.id}`} title={l.name} right={<div className="flex items-center gap-1.5">
      {s.killed ? <Pill tone="red">killed</Pill> : s.error_code ? <Pill tone="red">{s.error_code} · {s.error_for}s</Pill> : <Pill tone="green">serving</Pill>}
      {s.frozen && <Pill tone="amber">frozen</Pill>}{s.silent && <Pill tone="amber">silent</Pill>}<Pill tone="cyan">{s.audio}</Pill>
      <button data-testid={`lab-delete-${l.id}`} onClick={del} className="p-1 text-slate-500 hover:text-red-400"><Trash2 size={13} /></button>
    </div>}>
      <div className="mb-3">
        <UrlRow label="HLS" url={l.urls.hls} testid={`lab-url-hls-${l.id}`} />
        <UrlRow label="TS" url={l.urls.ts} testid={`lab-url-ts-${l.id}`} />
        <UrlRow label=".php (TS)" url={l.urls.php} testid={`lab-url-php-${l.id}`} />
        <UrlRow label="Redirect" url={l.urls.redirect} testid={`lab-url-redirect-${l.id}`} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {s.killed ? <Act {...p} action="revive" label="Revive" tone="green" testid={`lab-revive-${l.id}`} /> : <Act {...p} action="kill" label="Kill" tone="red" testid={`lab-kill-${l.id}`} />}
        {s.frozen ? <Act {...p} action="unfreeze" label="Unfreeze" tone="green" testid={`lab-unfreeze-${l.id}`} /> : <Act {...p} action="freeze" label="Freeze" testid={`lab-freeze-${l.id}`} />}
        {s.silent ? <Act {...p} action="unsilence" label="Unsilence" tone="green" testid={`lab-unsilence-${l.id}`} /> : <Act {...p} action="silence" label="Silence" testid={`lab-silence-${l.id}`} />}
        <Act {...p} action="reset_ts" label="Reset timestamps" testid={`lab-reset-ts-${l.id}`} />
        {["aac", "ac3", "eac3"].filter((a) => a !== s.audio).map((a) => <Act key={a} {...p} action={a} label={`Audio → ${a.toUpperCase()}`} testid={`lab-audio-${a}-${l.id}`} />)}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <Input data-testid={`lab-seconds-${l.id}`} type="number" value={secs} onChange={(e) => setSecs(e.target.value)} className="w-20 h-7 bg-[#0B111B] border-slate-700 font-mono text-xs" />
        <span className="text-xs text-slate-500">s</span>
        <Act {...p} action="error" label="Return 404" extra={{ seconds: Number(secs), code: 404 }} testid={`lab-404-${l.id}`} />
        <Act {...p} action="error" label="Return 503" extra={{ seconds: Number(secs), code: 503 }} testid={`lab-503-${l.id}`} />
        <Act {...p} action="redirect_ttl" label="Set redirect TTL" extra={{ seconds: Number(secs) }} testid={`lab-ttl-${l.id}`} />
        <span className="text-[11px] font-mono text-slate-500">ttl {s.redirect_ttl}s · restarts {s.restarts}</span>
      </div>
    </Panel>
  );
}

export default function Lab() {
  const { data, reload } = usePoll("/admin/lab", 3000);
  const [name, setName] = useState("");
  const create = async () => {
    try { await api.post("/admin/lab", { name: name || `Lab ${(data?.length || 0) + 1}` }); setName(""); toast.success("Synthetic source started"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Sources lab" sub="Synthetic upstreams (test pattern with running counter + tone). Break them on purpose to prove failover, slate and repair."
        right={<div className="flex gap-2"><Input data-testid="lab-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="name" className="w-44 h-9 bg-[#0F1722] border-slate-700" />
          <Button data-testid="new-lab-btn" onClick={create} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"><Plus size={15} /> Synthetic source</Button></div>} />
      {data && data.length === 0 && <Empty>No synthetic sources. Create one, then use its URLs as channel sources.</Empty>}
      <div className="grid xl:grid-cols-2 gap-4">{(data || []).map((l) => <LabCard key={l.id} l={l} reload={reload} />)}</div>
    </div>
  );
}
